import { ApiURL } from "../../../../../@WUM/core/component/const/ApiURL";
import { NotificationCommand } from "../../../../../@WUM/core/WebsocketNotification/interfaces/NotificationBridgeInterface";
import {convertEmptyStringsToNull} from "./helper/ConvertEmptyStringsToNull";

/**
 * Function signature for emitting a toast notification.
 * Provide this from your component via a hook, e.g.:
 * `const { showToast } = useToast();`
 */
export interface ToastFn {
    (cmd: NotificationCommand): void;
}

/**
 * Options for {@link makeSubmitHandler}.
 * Use either `payload` (static body) or `buildPayload` (derive from form).
 *
 * @template TPayload - Shape of the JSON payload body that will be sent to the API.
 */
export interface SubmitHelperOptions<TPayload> {
    /** Function to display a toast (must be passed from a component that can call hooks). */
    showToast: ToastFn;

    /** API route appended to `ApiURL()`. Default: `""` */
    route?: string;

    /** Optional id */
    id?: string;

    /** HTTP method. Default: `"POST"` */
    method?: "POST" | "PUT" | "PATCH";

    /** Extra request headers merged into the defaults. */
    headers?: Record<string, string>;

    /** Static payload body. Mutually exclusive with `buildPayload`. */
    payload?: TPayload;

    /**
     * Builder to derive the payload from the current HTML form.
     * If provided, it will be used instead of `payload`.
     */
    buildPayload?: (form: HTMLFormElement) => TPayload;

    /** Success toast message (plain text or HTML). Default: `"Gespeichert."` */
    successMessage?: string;

    /** Error toast message (plain text or HTML). Default: `"Speichern fehlgeschlagen."` */
    errorMessage?: string;

    /** Toast variant on success (mapped in your UI). Default: `"success"` */
    successVariant?: string;

    /** Toast variant on error (mapped in your UI). Default: `"danger"` */
    errorVariant?: string;

    /** Called with the parsed JSON result when the request succeeds. */
    onSuccess?: (result: any) => void;

    /** Called with the thrown error when the request fails. */
    onError?: (err: unknown) => void;

    /** Always called at the end (success or error). */
    onFinally?: () => void;

    /** If provided, will be set to `false` on success. */
    setUnsaved?: (v: boolean) => void;

    /** If provided, will be called with `false` on success. */
    onChangeUnsaved?: (v: boolean) => void;

    /** If provided, will be called with `true` when form validation fails. */
    setValidated?: (v: boolean) => void;

    /**
     * If true, all empty string values ("") in the payload
     * will be converted to null before sending the request.
     */
    setEmptyStringsToNull?: boolean;
}

/**
 * Creates a form submit handler that:
 *  - prevents default form submission
 *  - validates the form via `checkValidity()`
 *  - builds a JSON request body (via `buildPayload` or `payload`)
 *  - sends the request to `ApiURL() + route`
 *  - shows success/error toasts
 *  - resets "unsaved" state on success
 *
 * @template TPayload - Shape of the JSON payload body.
 * @param opts - Configuration for the submit flow.
 * @returns An async submit handler suitable for `<form onSubmit={...}>` or your `UniForm`'s `Submit` prop.
 *
 * @example
 * ```ts
 * // inside your component:
 * const { showToast } = useToast();
 *
 * const handleSubmit = makeSubmitHandler({
 *   showToast,
 *   route: "work/notifications/save/",
 *   buildPayload: (form) => ({
 *     activateAdminIrregularityMail: (form as any).activateAdminIrregularityMail.checked,
 *     activateDayEndReminderPopup: (form as any).activateDayEndReminderPopup.checked,
 *     dayEndReminderTime: (form as any).dayEndReminderTime.value,
 *     activateDashboardInfoBanner: (form as any).activateDashboardInfoBanner.checked,
 *     dashboardInfoBannerContent: editorHtml, // e.g. from SunEditor
 *   }),
 *   successMessage: "Settings saved.",
 *   errorMessage: "Save failed.",
 *   setUnsaved,
 *   onChangeUnsaved,
 *   setValidated,
 *   normalizeEmptyStringsToNull: true
 * });
 *
 * // in JSX:
 * <UniForm Submit={handleSubmit}>
 *   ...
 * </UniForm>
 * ```
 */
export function makeSubmitHandler<TPayload>(opts: SubmitHelperOptions<TPayload>) {
    const {
        showToast,
        route = "",
        id,
        method = "POST",
        headers,
        payload,
        buildPayload,
        successMessage = "Gespeichert.",
        errorMessage = "Speichern fehlgeschlagen.",
        successVariant = "success",
        errorVariant = "danger",
        onSuccess,
        onError,
        onFinally,
        setUnsaved,
        onChangeUnsaved,
        setValidated,
        setEmptyStringsToNull
    } = opts;

    return async (event: React.FormEvent<HTMLFormElement>) => {
        // Prevent the browser from navigating away and stop bubbling.
        event.preventDefault();
        event.stopPropagation();

        // Guard: require a valid form before sending.
        const form = event.currentTarget;
        if (!form.checkValidity()) {
            setValidated?.(true);
            return;
        }

        // Read auth/user context (adjust to your environment as needed).
        const token = JSON.parse(localStorage.getItem("token") || '""');
        const userId = Number(JSON.parse(localStorage.getItem("userid") || "0"));

        // build route
        const baseWithSlash = route.endsWith("/") ? route : route + "/";
        const finalRoute = (id !== undefined && id !== null && String(id).length > 0)
            ? `${baseWithSlash}${id}/`
            : route;

        // Build request body: prefer builder if provided, fall back to static payload.
        let body: TPayload = buildPayload ? buildPayload(form) : (payload as TPayload);

        if (setEmptyStringsToNull && body && typeof body === "object") {
            body = convertEmptyStringsToNull(body as Record<string, any>) as TPayload;
        }

        // Prepare request options.
        const requestOptions: RequestInit = {
            method,
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
                ...(headers ?? {}),
            },
            body: JSON.stringify(body),
        };

        try {
            // Send request and check status.
            const res = await fetch(ApiURL() + finalRoute, requestOptions);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            // Parse response JSON (adjust if your API returns no body).
            const result = await res.json();

            // Emit success toast.
            showToast({
                func: "showToast",
                type: "toast",
                settings: ["variant", successVariant],
                content: successMessage,
                user: userId,
            });

            // Reset "unsaved" UI state if provided.
            setUnsaved?.(false);
            onChangeUnsaved?.(false);

            // Let the caller react to the successful result.
            onSuccess?.(result);
        } catch (err) {
            // Emit error toast.
            showToast({
                func: "showToast",
                type: "toast",
                settings: ["variant", errorVariant],
                content: errorMessage,
                user: userId,
            });

            // Let the caller handle the error as needed.
            onError?.(err);

            // Keep the console message for diagnostics (adjust language as desired).
            console.error("Fehler beim Speichern:", err);
        } finally {
            // Always run the finalizer.
            onFinally?.();
        }
    };
}
